import $ from 'jquery';
import { nodeConfig } from '@/mixins';
import { mod, afterTransition } from '@/helpers/utils';
import { Swap } from '@/plugins/swap';

const NAMESPACE = 'share-tools';
const DEFAULT_SELECTOR = '[data-js-share-tools]';
const DEFAULT_SETTINGS = {
	//
};

class ShareTools {
	constructor(node, config) {
		this.$node = $(node);
		this.config = config;

		this.url = window.location.href;
		this.title = encodeURIComponent($('title').text());
		this.description = $('meta[property="og:description"]').attr('content');

		this.$option = this.$node.find('[data-js-share-tools-list]').first();
		this.$toggle = this.$node.find('[data-js-share-tools-toggle]');
		this.$tools = this.$node.find('[data-js-share-tool]');
		this.$toolItems = this.$node.find('[data-js-share-tools-item]');

		this.$share = {
			facebook: this.$node.find('[data-js-share-tool="facebook"]'),
			x: this.$node.find('[data-js-share-tool="x"]'),
			linkedin: this.$node.find('[data-js-share-tool="linkedin"]')
		};
	}

	get supportsNavigator() {
		return typeof navigator.share !== 'undefined';
	}

	get isActive() {
		return this.$node.hasClass('state-swap-active');
	}

	closeOptions() {
		this.$toggle.trigger('focus');
		this.$option.attr({ inert: '', 'aria-hidden': 'true' });
		this.$tools.attr('tabindex', '-1');
	}

	openOptions() {
		this.$option.removeAttr('inert').attr('aria-hidden', 'false');
		this.$tools.removeAttr('tabindex');
	}

	shareViaNavigator() {
		navigator.share({
			title: this.title,
			text: this.description,
			url: this.url
		});
	}

	shareViaPopout() {
		if (!this.isActive) {
			this.closeOptions();

			return;
		}

		this.openOptions();

		afterTransition(this.$toolItems.first()[0], 'opacity', () => {
			this.$option.first().find(this.$tools).first().trigger('focus');
		});
	}

	onToggleClick() {
		if (this.supportsNavigator) {
			this.shareViaNavigator();

			return;
		}

		this.shareViaPopout();
	}

	onToggleKeydown(event) {
		if (this.supportsNavigator) return;

		let keyCode = event.key.toLowerCase();

		if (!['arrowup', 'arrowdown'].includes(keyCode)) return;

		event.preventDefault();

		if (!this.isActive) {
			Swap.activate(this.$toggle[0]);

			this.openOptions();
		}

		let lookup = {
			arrowup: () => {
				afterTransition(this.$toolItems.first()[0], 'opacity', () => {
					this.$option.find(this.$tools).last().trigger('focus');
				});
			},
			arrowdown: () => {
				afterTransition(this.$toolItems.last()[0], 'opacity', () => {
					this.$option.find(this.$tools).first().trigger('focus');
				});
			}
		};

		if (!this.isActive) return;

		if (keyCode in lookup) {
			lookup[keyCode]();
		}
	}

	onOptionsKeydown(event) {
		let keyCode = event.key.toLowerCase();

		if (!['escape', 'arrowleft', 'arrowright', 'home', 'end'].includes(keyCode)) {
			return;
		}

		event.preventDefault();

		let $focused = $(':focus').closest(this.$toolItems);
		let focusedIndex = $focused.index();

		let lookup = {
			escape: () => {
				Swap.deactivate(this.$toggle[0]);
				this.$toggle.trigger('focus');
			},
			arrowleft: () => {
				let prevIndex = mod(focusedIndex - 1, this.$toolItems.length);
				this.$toolItems.eq(prevIndex).find('[data-js-share-tool]').trigger('focus');
			},
			arrowright: () => {
				let nextIndex = mod(focusedIndex + 1, this.$toolItems.length);
				this.$toolItems.eq(nextIndex).find('[data-js-share-tool]').trigger('focus');
			},
			home: () => {
				this.$tools.first().trigger('focus');
			},
			end: () => {
				this.$tools.last().trigger('focus');
			}
		};

		if (keyCode in lookup) {
			lookup[keyCode]();
		}
	}

	bindUI() {
		this.$toggle.on({
			click: this.onToggleClick.bind(this),
			keydown: this.onToggleKeydown.bind(this)
		});

		if (!this.supportsNavigator) {
			this.$tools.on('keydown', this.onOptionsKeydown.bind(this));
		}
	}

	setup() {
		this.$share.facebook.attr(
			'href',
			`https://www.facebook.com/sharer/sharer.php?u=${this.url}`
		);
		this.$share.x.attr('href', `https://x.com/intent/tweet?text=${this.title}&url=${this.url}`);
		this.$share.linkedin.attr(
			'href',
			`https://www.linkedin.com/shareArticle?mini=true&url=${this.url}&title=${this.title}`
		);

		this.$option.attr({ inert: '', 'aria-hidden': 'true' });

		this.$tools.attr('tabindex', '-1');

		if (!this.supportsNavigator) {
			new Swap(this.$toggle[0], {
				target: this.$node[0],
				triggerAttributes: [
					{
						attribute: 'aria-expanded',
						on: 'true',
						off: 'false'
					}
				]
			});

			this.$toggle.attr({ 'aria-haspopup': 'true' });
		}
	}

	init() {
		this.setup();
		this.bindUI();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	let listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		let config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		let module = new ShareTools(node, config);

		module.init();

		return module;
	});
}
